import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/salesOrderMutations"

import {
  type SalesOrder,
  SalesOrderStatus,
  type SalesOrderUpdateError,
  type SalesOrderUpdateFailure,
  type SalesOrderUpdateInput,
  UpdateSalesOrderDocument,
  type UpdateSalesOrderMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { SALES_ORDER_MISSING_FIELD_TO_LABEL } from "@/screens/CustomerOrders/constants"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { PERMISSION_DENIED_MUTATION_MESSAGE, isForbiddenError } from "@/utils/urql/permissionError"

interface UpdateSalesOrderOptions {
  successMessage?: string
  successType?: NotificationType
}

interface UseUpdateSalesOrderReturn {
  updateSalesOrder: (input: SalesOrderUpdateInput, options?: UpdateSalesOrderOptions) => Promise<SalesOrder | undefined>
  isSaving: boolean
}

const toFailureMessage = ({ message, missingFields }: SalesOrderUpdateError, input: SalesOrderUpdateInput): string => {
  const isConfirming = input.status === SalesOrderStatus.Confirmed
  if (!isConfirming || !missingFields?.length) return message

  const fieldLabels = missingFields.map((field) => SALES_ORDER_MISSING_FIELD_TO_LABEL[field] ?? field)
  return `${fieldLabels.join(", ")} required to confirm`
}

const getSuccessMessage = (input: SalesOrderUpdateInput, savedSalesOrder?: SalesOrder): string => {
  if (!savedSalesOrder || !input.lines) return "Changes saved"

  const savedLineIds = new Set(savedSalesOrder.lines.map((line) => line.id))
  const hasMovedLines =
    savedSalesOrder.salesOrderId !== input.salesOrderId || input.lines.some((line) => !savedLineIds.has(line.lineId))

  return hasMovedLines ? "Changes saved. Some lines were moved to another Sales Order." : "Changes saved"
}

export const useUpdateSalesOrder = (): UseUpdateSalesOrderReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<UpdateSalesOrderMutation, { input: SalesOrderUpdateInput }>(
    UpdateSalesOrderDocument
  )

  const updateSalesOrder = useCallback(
    async (
      input: SalesOrderUpdateInput,
      { successMessage, successType = NotificationType.Success }: UpdateSalesOrderOptions = {}
    ): Promise<SalesOrder | undefined> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.salesOrder.update
      const hasError = error || output?.__typename === "SalesOrderUpdateFailure"

      if (hasError) {
        const message = isForbiddenError(error)
          ? PERMISSION_DENIED_MUTATION_MESSAGE
          : (error?.message ?? toFailureMessage((output as SalesOrderUpdateFailure).error, input))
        notify({ type: NotificationType.Error, message })
        return undefined
      }

      const savedSalesOrder =
        output?.__typename === "SalesOrderUpdateSuccess" ? (output.salesOrder as SalesOrder) : undefined

      notify({ type: successType, message: successMessage ?? getSuccessMessage(input, savedSalesOrder) })
      return savedSalesOrder
    },
    [executeMutation, notify]
  )

  return { updateSalesOrder, isSaving: fetching }
}
